<template>
  <div class="custom-code-task-wrapper">
    <div
      class="modal task custom-code-modal"
      :class="{
        'full-screen': isModalFullScreen,
        'edit-custom-task-visible': isCustomEditModalVisible,
      }"
    >
      <div class="modal-header-actions">
        <div
          class="fullscreen-icon"
          v-b-tooltip.hover.top
          title="Vollbild"
          @click="modalFullScreen()"
        >
          <i v-if="!isModalFullScreen" class="fas fa-expand"></i>
          <i v-else class="fas fa-compress"></i>
        </div>
        <div class="modal-close" @click="close"></div>
      </div>
      <div
        class="round-button item modal-run-action"
        v-if="!isCustomEditModalVisible"
        v-b-tooltip.hover.bottom
        title="Ausführen"
        @click="!loading && runCode()"
      >
        <b-spinner v-if="loading" small label="Spinning"></b-spinner>
        <i v-else class="fas fa-play"></i>
      </div>
      <div class="custom-modal-header">
        {{ task?.caption || 'custom code' }}
      </div>
      <div
        class="content custom-task-inputs"
        v-if="isComponentRendered && !isCustomEditModalVisible"
      >
        <div class="custom-code-modal-wrapper">
          <div class="header">
            <div class="tools">
              <div
                class="round-button item"
                @click="toggleCredentialModal()"
                v-b-tooltip.hover.bottom
                title="Sicher"
              >
                <i class="fa fa-key"></i>
              </div>
              <div
                @click="toggleCustomEditModal(false)"
                v-b-tooltip.hover.bottom
                title="Bearbeiten"
                class="round-button item"
                v-if="
                  isUserHasAccess && getCustomCodeTaskId && customTaskDetails
                "
              >
                <i class="fa fa-edit"></i>
              </div>
              <div
                class="round-button item"
                @click="toggleLogsModal()"
                v-b-tooltip.hover.bottom
                title="Logs"
              >
                <img
                  src="../../assets/img/work-flow-icons/log.svg"
                  alt="log"
                  class="log-icon"
                />
              </div>
            </div>
          </div>
          <div class="code-tool-wrapper">
            <div class="task-tools"></div>
          </div>
          <div class="custom-code-modal-content-wrapper">
            <div class="custom-task-detail tab" v-if="!customTaskLoading">
              <div
                class="input-area fancy-scroll"
                v-if="customCodeDetails.inputs.length > 0"
                @scroll.passive="showScrollbar"
              >
                <div
                  v-for="data in customCodeDetails.inputs"
                  :key="data.name"
                  class="read-only"
                >
                  <div class="input-attributes">
                    <h3>{{ data.label }}</h3>
                    <div
                      v-if="jsonTypes.includes(data.type)"
                      class="input-wrap"
                    >
                      <input
                        type="text"
                        :name="data.name"
                        :value="getValidValue(data)"
                        @input="handleInputChange(data, $event.target.value)"
                      />
                    </div>
                    <div v-else class="input-wrap">
                      <input
                        type="text"
                        :name="data.name"
                        v-model="data.value"
                      />
                    </div>
                    <div
                      v-if="getDropdownInputItems.length"
                      class="custom-dropdown"
                    >
                      <generalDropdown>
                        <template v-slot:optionData>
                          <b-dropdown-item
                            v-for="item in getDropdownInputItems"
                            :key="item.name"
                            @click="onOptionSelect(item, data)"
                          >
                            {{ item.name }}
                          </b-dropdown-item>
                        </template>
                      </generalDropdown>
                    </div>
                  </div>
                </div>
              </div>
              <div class="output-area">
                <h1>Output:</h1>
                <div class="output-read-only">
                  <h3
                    v-for="data in task.output"
                    :key="data.name"
                    class="read-only"
                  >
                    {{ data.name }} : {{ data.value }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CodeTaskLogsModal
        v-if="isLogModalVisible"
        :processId="process.id"
        :taskId="task.id"
        @onClose="isLogModalVisible = false"
      />
      <CodeCredentialSidebar
        v-if="isCredentialModalVisible"
        :credentialList="credentialList"
        :processId="process.id"
        @close="toggleCredentialModal"
      />
      <LoadingSpinner
        loadingCaption="Wird geladen..."
        v-if="customTaskLoading || !isComponentRendered"
        class="outer-loader"
      />
      <MakeCustomTaskComponent
        v-if="isCustomEditModalVisible && customTaskDetails"
        :isEditMode="true"
        :task="task"
        :process="process"
        :flowRunnerEndpoint="flowRunnerEndpoint"
        @closeEditModal="toggleCustomEditModal"
        :editCodeDetails="task.props"
        :customTask="customTaskDetails"
        :inputFields="task.input"
        @showToaster="showToaster"
        @triggerCustomTaskItemFetch="triggerCustomTaskItemFetch"
        @updateCustomTaskDetails="updateCustomTaskDetails"
      />
    </div>
  </div>
</template>

<script>
import codeEditor from '../widgets/codeEditor.vue';
import MakeCustomTaskComponent from '../widgets/MakeCustomTaskComponent.vue';
import GeneralDropdown from '../core/general-dropdown.vue';
import CodeTaskLogsModal from '../modals/CodeTaskLogsModal.vue';
import LoadingSpinner from '../widgets/LoadingSpinner.vue';
import {
  debouncer,
  jsonParser,
  getInputPlaceholderList,
  getApiEndpointVersion,
  LATEST_FLOW_RUNNER_VERSION,
  flattenTaskInputs,
} from '../../utils/helpers';
import CodeCredentialSidebar from '@/components/modals/CodeCredentialSidebar.vue';
import { mapGetters } from 'vuex';
import {
  getCustomDatabaseItems,
  getCustomDatabaseItem,
} from '@/services/axios/data.api';
export default {
  name: 'CustomCode',
  data() {
    return {
      loading: false,
      isComponentRendered: false,
      jsonTypes: ['object', 'objectlist', 'model', 'json', 'list'],
      modelTypes: [
        'timeseries', // timeseries is needed for backwards compatiblility
        'loadprofile',
        'custom',
        'model',
      ],
      credentialList: [],
      listTypes: ['checkbox'],
      isModalFullScreen: false,
      customCodeDetails: null,
      customTaskDetails: null,
      customTaskLoading: false,
      isLogModalVisible: false,
      isCredentialModalVisible: false,
      isCustomEditModalVisible: false,
    };
  },
  props: {
    task: Object,
    process: Object,
    isUserHasAccess: {
      type: Boolean,
      required: true,
      default: false,
    },
    flowRunnerEndpoint: {
      type: String,
    },
  },
  methods: {
    close: function () {
      if (this.isCustomEditModalVisible) {
        this.toggleCustomEditModal(false);
        return;
      }
      this.$set(this.task, 'output', this.task.output.concat(this.task.input));
      this.$set(this.task, 'props', this.customCodeDetails);
      this.$emit('close');
    },
    handleInputChange(data, value) {
      const parseJsonData = jsonParser(value);
      this.$set(data, 'value', parseJsonData || value);
    },
    getValidValue(data) {
      if (data.value && typeof data.value === 'object') {
        return JSON.stringify(data.value);
      }
      return data.value;
    },
    isCredentialAllowedForProcess(credential, processId = this.process?.id) {
      const workflowDataIdList = credential?.workflowdataidlist;
      const normalizedProcessId = String(processId);

      if (Array.isArray(workflowDataIdList)) {
        return workflowDataIdList.some(
          (id) => String(id) === normalizedProcessId
        );
      }

      if (typeof workflowDataIdList === 'string') {
        return workflowDataIdList
          .split(',')
          .map((id) => id.trim())
          .includes(normalizedProcessId);
      }

      return false;
    },
    parseCredentialPlaceholder(placeholderName) {
      if (typeof placeholderName !== 'string') return null;

      const placeholderParts = placeholderName.split(':');
      if (placeholderParts.length < 3) return null;

      const prefix = placeholderParts[0];
      const field = placeholderParts[placeholderParts.length - 1];
      const keyname = placeholderParts.slice(1, -1).join(':');

      if (
        prefix !== 'codelos_creds' ||
        !keyname ||
        !['username', 'password'].includes(field)
      ) {
        return null;
      }

      return { keyname, field };
    },
    resolveCredentialPlaceholderValue(placeholderName) {
      const parsedPlaceholder =
        this.parseCredentialPlaceholder(placeholderName);
      if (!parsedPlaceholder) return null;

      const matchedCredential = (this.credentialList || []).find(
        (credential) =>
          String(credential?.keyname) === String(parsedPlaceholder.keyname) &&
          this.isCredentialAllowedForProcess(credential)
      );

      if (
        !matchedCredential ||
        matchedCredential[parsedPlaceholder.field] == null
      ) {
        return null;
      }

      return matchedCredential[parsedPlaceholder.field];
    },
    getCredentialDropdownItems() {
      return (this.credentialList || []).flatMap((credential) => {
        if (!this.isCredentialAllowedForProcess(credential)) return [];

        const keyname = credential?.keyname;
        return [
          {
            label: `codelos_creds:${keyname}:username`,
            name: `codelos_creds:${keyname}:username`,
            value: credential?.username,
          },
          {
            label: `codelos_creds:${keyname}:password`,
            name: `codelos_creds:${keyname}:password`,
            value: credential?.password,
          },
        ];
      });
    },
    setValidValue(key, item, formData, isCheck = false) {
      if (item.type == 'file') formData.append(key, item.value);
      else if (this.jsonTypes.includes(item.type.toLowerCase())) {
        formData.append(
          key,
          typeof item.value === 'string'
            ? item.value
            : JSON.stringify(item.value)
        );
      } else if (this.modelTypes.includes(item.type)) {
        formData.append(key, item?.value?.id);
      } else if (this.listTypes.includes(item.type)) {
        if (isCheck && formData.has(key)) return;
        const parseJsonData = jsonParser(item.value);
        const validArray = Array.isArray(item.value)
          ? item.value
          : typeof item.value === 'string'
          ? Array.isArray(parseJsonData)
            ? parseJsonData
            : item.value.split(',')
          : [item.value];

        validArray.forEach((el) => formData.append(key, el));
      } else {
        formData.append(key, item.value);
      }
    },
    isInputNameReferencedInProps(inputName) {
      if (!inputName) return false;

      const props = this.task?.props || this.customCodeDetails || {};
      const code = props?.code;
      const propsInput = props?.input || props?.inputs;

      const isReferencedInCode =
        typeof code === 'string' && code.includes(inputName);
      const isReferencedInInput =
        propsInput != null && JSON.stringify(propsInput).includes(inputName);

      return isReferencedInCode || isReferencedInInput;
    },
    runCode: function () {
      this.loading = true;

      // As flowrunner expects only first task, remove other tasks
      // and clear output
      let f = JSON.parse(JSON.stringify(this.process));
      f.tasks = [f.tasks[0]];
      f.tasks[0].output = [];
      // Put props and input in form data
      var formData = new FormData();

      const flattenedTaskInputs = flattenTaskInputs(this.task.input);
      //As select CustomTaskDatabase props changed
      if (this.customCodeDetails) {
        this.customCodeDetails.inputs.forEach((e) => {
          const match = e.value?.match?.(/^\{(.+)\}$/);
          const inputName = match?.[1];
          let selectedInput =
            inputName && flattenedTaskInputs.find((i) => i.name === inputName);

          if (!selectedInput && typeof inputName === 'string') {
            const credentialValue =
              this.resolveCredentialPlaceholderValue(inputName);
            if (credentialValue != null) {
              selectedInput = {
                ...e,
                value: credentialValue,
              };
            }
          }

          this.setValidValue(e.name, selectedInput || e, formData);
        });
      }

      this.task.props = this.customCodeDetails;
      formData.append('flow', JSON.stringify(f));
      formData.append('props', JSON.stringify(this.task.props));
      formData.append('task-id', this.task.id);
      var myHeaders = new Headers();
      myHeaders.append(
        'Authorization',
        'Bearer ' + this.user.signInUserSession.accessToken.jwtToken
      );

      if (this.task.input) {
        this.task.input.forEach((e) => {
          if (!this.isInputNameReferencedInProps(e?.name)) return;
          this.setValidValue(e.name, e, formData, true);
        });
      }

      // Call API
      var url = this.flowRunnerEndpoint;
      url += '/run_code';

      fetch(url, {
        body: formData,
        headers: myHeaders,
        method: 'post',
      })
        .then(async (response) => {
          if (response.status == 500) {
            let jsn = await response.json().then((jsn) => {
              return jsn;
            });
            throw new Error(jsn['message']);
          }
          if (response.status != 200) throw new Error(response.statusText);
          else return response.json();
        })
        .then((json) => {
          this.task.output = json;

          if (json.filter((e) => e.label == 'error').length > 0) {
            this.showToaster(
              'Bitte beheben Sie den Fehler im Code !',
              'Fehlerhaft !',
              'danger'
            );
            this.$set(this.task, 'status', 'error');
          } else {
            this.$set(this.task, 'status', 'success');
            this.$emit('resetErrorMessage');
          }

          this.loading = false;
        })
        .catch((e) => {
          this.task.output = [
            {
              label: 'error',
              name: 'error',
              type: 'text',
              value: e,
            },
          ];

          this.showToaster(
            'Bitte beheben Sie den Fehler im Code !',
            'Fehlerhaft !',
            'danger'
          );
          this.loading = false;
        });
    },
    getTSDisplayFormat: function (ts) {
      // Make deepcopy
      let copy = JSON.parse(JSON.stringify(ts));

      // Modify data for display purpose
      let d = copy.data.slice(0, 2);
      d.push(['...'], ['...']);
      copy.data = d;

      return copy;
    },
    isObjectTimeseries: function (o) {
      let a = o.hasOwnProperty('ts_type');
      let b = o.hasOwnProperty('supplyPoint');
      let c = o.hasOwnProperty('quantity');
      let d = o.hasOwnProperty('convention');

      return a + b + c + d;
    },
    toggleCustomEditModal(isClose = false) {
      this.isCustomEditModalVisible = !this.isCustomEditModalVisible;
      if (isClose) {
        this.customCodeDetails = this.task.props;
      }
    },
    showToaster(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
      });
    },
    onOptionSelect(item, element) {
      const selectedInput =
        this.task.input?.find(
          (input) => input.label === (item?.label || item)
        ) || this.task.input?.find((input) => input.name === item?.name);
      const placeholderName = selectedInput?.name || item?.name || item;
      if (placeholderName) {
        element.value = `{${placeholderName}}`;
      }
      this.$forceUpdate();
    },
    showScrollbar(event) {
      event.target.classList.add('show-scrollbar');
      debouncer(
        () => {
          event.target.classList.remove('show-scrollbar');
        },
        300,
        this,
        'removeScrollClass'
      );
    },
    toggleLogsModal() {
      this.isLogModalVisible = !this.isLogModalVisible;
    },
    toggleCredentialModal() {
      this.isCredentialModalVisible = !this.isCredentialModalVisible;
    },
    async setCustomkeysDatabaseItemList() {
      try {
        if (!this.getCustomKeysDBDetails?.name) return;
        this.isComponentRendered = false;
        const response = await getCustomDatabaseItems(
          this.getCustomKeysDBDetails.name
        );
        if (response?.data?.items?.length)
          this.credentialList = response.data.items;
      } catch (error) {
        console.log('set error', error);
      } finally {
        this.isComponentRendered = true;
      }
    },
    async setCustomTaskDetails() {
      try {
        this.isComponentRendered = false;
        let customTaskId = this.task?.props?.customTaskId;

        if (!customTaskId && this.getCustomTaskDatabase?.name) {
          const response = await getCustomDatabaseItems(
            this.getCustomTaskDatabase.name
          );
          const items = response?.data?.items || [];
          const matchedTask = items.find((item) => {
            const codeMatches =
              item?.code?.code === this.customCodeDetails?.code;
            const descriptionMatches =
              item?.description === this.customCodeDetails?.description;
            const nameMatches = item?.taskname === this.customCodeDetails?.name;
            const inputsMatch =
              JSON.stringify(item?.inputfields || []) ===
              JSON.stringify(this.customCodeDetails?.inputs || []);

            return (
              codeMatches && descriptionMatches && nameMatches && inputsMatch
            );
          });
          if (matchedTask?.id) {
            customTaskId = matchedTask.id;
            this.$set(this.task.props, 'customTaskId', matchedTask.id);
          }
        }

        if (!customTaskId) return;

        const response = await getCustomDatabaseItem(
          this.getCustomTaskDatabase?.name,
          customTaskId,
          true
        );
        if (response.data && response.data?.message == 'Not found') return;
        this.customTaskDetails = response.data;
      } catch (error) {
        console.log('error :>> ', error);
        if (error?.data && error.data?.message == 'Not found') return;
      } finally {
        this.isComponentRendered = true;
      }
    },
    modalFullScreen() {
      this.isModalFullScreen = !this.isModalFullScreen;
    },
    triggerCustomTaskItemFetch(id) {
      this.$emit('tiggerSpecificEvent', { name: 'fetchCustomTaskItem', id });
    },
    updateCustomTaskDetails(updatedTask) {
      this.customTaskDetails = updatedTask;
      if (updatedTask?.id) {
        this.$set(this.task.props, 'customTaskId', updatedTask.id);
      }
    },
  },
  created() {
    this.task.output = [];
    this.customCodeDetails = this.task.props;
    this.setCustomkeysDatabaseItemList();
    this.setCustomTaskDetails();
  },
  computed: {
    ...mapGetters({
      databaseList: 'getDatabaseList',
    }),
    user() {
      return this.$store.state.user;
    },
    getDropdownInputItems() {
      const apiVersion =
        getApiEndpointVersion(this.flowRunnerEndpoint) ||
        LATEST_FLOW_RUNNER_VERSION;

      const dropdownItems =
        getInputPlaceholderList(
          this.task.input,
          apiVersion,
          ['dataframe'],
          true
        ) || [];

      dropdownItems.push(...this.getCredentialDropdownItems());
      return dropdownItems;
    },
    getCustomKeysDBDetails() {
      return this.databaseList.find(
        (database) => database.type == 'custom-keys'
      );
    },
    getCustomTaskDatabase() {
      return this.databaseList.find(
        (database) => database.type == 'custom-task'
      );
    },
    getCustomCodeTaskId() {
      return this.task?.props?.customTaskId;
    },
  },
  directives: {
    clickOutside: {
      bind: function (el, binding, vnode) {
        el.eventSetDrag = function () {
          el.setAttribute('data-dragging', 'yes');
        };
        el.eventClearDrag = function () {
          el.removeAttribute('data-dragging');
        };
        el.eventOnClick = function (event) {
          var dragging = el.getAttribute('data-dragging');
          // Check that the click was outside the el and its children, and wasn't a drag
          if (!(el == event.target || el.contains(event.target)) && !dragging) {
            // call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.addEventListener('touchstart', el.eventClearDrag);
        document.addEventListener('touchmove', el.eventSetDrag);
        document.addEventListener('click', el.eventOnClick);
        document.addEventListener('touchend', el.eventOnClick);
      },
      unbind: function (el) {
        document.removeEventListener('touchstart', el.eventClearDrag);
        document.removeEventListener('touchmove', el.eventSetDrag);
        document.removeEventListener('click', el.eventOnClick);
        document.removeEventListener('touchend', el.eventOnClick);
        el.removeAttribute('data-dragging');
      },
    },
  },
  components: {
    codeEditor,
    LoadingSpinner,
    GeneralDropdown,
    CodeTaskLogsModal,
    CodeCredentialSidebar,
    MakeCustomTaskComponent,
  },
};
</script>

<style scoped>
.custom-code-task-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.custom-code-task-wrapper .modal.task {
  padding-inline: 34px !important;
}
.custom-code-task-wrapper .modal.task.edit-custom-task-visible {
  padding-inline: 0 !important;
}
.custom-code-task-wrapper .modal-header-actions {
  position: absolute;
  top: 32px;
  right: 36px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.custom-code-task-wrapper .fullscreen-icon {
  position: static;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 16px;
  color: #000;
  cursor: pointer;
}
.custom-code-task-wrapper .modal-header-actions .modal-close {
  position: static !important;
}
.custom-code-task-wrapper .modal-run-action {
  position: absolute;
  top: 32px;
  left: 36px;
}
.heading {
  padding: 15px 15px 0px;
  text-align: center;
}

.content {
  background-color: #fff;
  width: 100%;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  text-align: center;
  height: calc(100% - 36px - 17px);
  margin-top: 17px;
}

.tab {
  text-align: left;
  padding: 31px;
  height: 100%;
}

.tab .notes {
  float: right;
  margin-top: 20px;
}
.tab .notes p {
  color: rgb(247, 57, 89);
  font-size: 13px;
}

/* code-editor */
.dragable-wrapper {
  position: relative;
}
.dragable-line-first {
  position: absolute;
  display: inline-block;
  width: 100%;
  height: 5px;
  background-color: #fff;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0.5;
  cursor: row-resize;
  z-index: 99;
  user-select: none;
}

.dragable-line-second {
  position: absolute;
  display: inline-block;
  width: 100%;
  height: 5px;
  background-color: #fff;
  opacity: 0.5;
  cursor: row-resize;
  z-index: 9;
}
.editor-wrapper.read-only {
  height: 150px;
}
.tab .editor-wrapper:first-child {
  margin-top: 0;
}
.editor-wrapper {
  margin-top: 25px;
  height: 350px;
  margin-left: auto;
  margin-right: auto;
  overflow-y: scroll;
  width: 100%;
  border-radius: 4px;
}

.editor-wrapper.read-write .prism-editor-wrapper.code-editor {
  min-height: 450px;
  height: 100%;
  padding-top: 0;
}

::v-deep .prism-editor__container {
  height: fit-content;
  min-height: 450px;
}

.select-language {
  margin-top: 50px;
  width: 100%;
  text-align: right;
}

.select-language select {
  width: 110px;
  height: 42px;
  font-size: 18px;
  border: none;
  background: transparent;
}
.modal.loader {
  overflow: hidden;
}

.heading {
  padding: 15px 15px 0px;
  text-align: center;
}

h1 {
  padding-top: 50px;
  padding-bottom: 8px;
  text-align: center;
  font-size: 33px;
  color: #464d5d;
  margin: 0 0 12px;
  font-weight: 400;
  line-height: 35px;
  text-rendering: optimizelegibility;
  -webkit-font-smoothing: antialiased;
}

h3 {
  color: rgba(85, 93, 109, 0.4);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
}

h4 {
  color: rgba(85, 93, 109, 0.4);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0px;
}

input {
  float: left;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  padding-bottom: 15px;
  margin-bottom: 0;
  height: 51px;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 20px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

textarea {
  float: left;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  padding-bottom: 15px;
  margin-bottom: 0;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 20px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  height: 200px;
  width: 100%;
  overflow: scroll;
}

button {
  border: 1px solid #dee3e9;
  border-radius: 3px 3px 3px 3px;
  width: 30%;
  height: 51px;
  text-transform: uppercase;
  font-weight: 600;
}

button.default {
  color: #464d5d;
  background: #fff;
}

.input-group {
  width: 100%;
}

.imput-group > input {
  width: 70%;
}

.input-group > button {
  min-width: 0;
  width: 30%;
  height: 51px;
  text-transform: uppercase;
  font-weight: 600;
  color: #fff;
  background: #6bbc7c;
  border: 1px solid #5ab36c;
  margin-bottom: 0;
  border-radius: 0 3px 3px 0;
}

.pull-right {
  float: right;
}

.modal .btn-close {
  margin-left: 14px;
  border: none;
  display: block;
  width: 20px;
  top: 0;
  height: 14px;
  margin-top: 0;
  opacity: 1;
  box-sizing: border-box;
}

button.btn-green {
  color: white;
  background: #4aae9b;
  border: 1px solid #4aae9b;
  border-radius: 2px;
  position: relative;
}

ul.tabsLabels {
  display: inline-block;
  padding: 0;
  margin: 0 0 -1px;
}

.modal ul.tabsLabels li {
  display: inline-block;
  margin: 0 18px;
  list-style: none;
  padding: 0;
  line-height: 14px;
}

.modal:not(.custom) li {
  font-size: 14px;
  line-height: 25px;
  color: #7e8890;
}

.modal ul.tabsLabels a {
  color: #a4abb1;
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  padding: 21px 7px 22px 33px;
  border-bottom: 2px solid transparent;
  display: block;
  position: relative;
}

a {
  text-decoration: none;
  -webkit-transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  -o-transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  cursor: pointer;
}

a {
  background-color: transparent;
}

.modal ul.tabsLabels a.active {
  border-color: #52b266;
}

.modal ul.tabsLabels a.active,
.modal ul.tabsLabels a:hover {
  color: #464d5d;
  text-decoration: none;
}

/*@media not all, not all, (-webkit-min-device-pixel-ratio: 1.5), not all, (min-resolution: 1.5dppx)*/
.tabsLabels .url {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -893px -1109px;
}

.tabsLabels .email {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -1100px -1110px;
}

.tabsLabels .mscons {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -828px -1175px;
}

.tabsLabels .system {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -137px -1178px;
}

p {
  font-weight: 500;
  font-size: 12px;
  line-height: 21px;
  color: #7e8890;
  margin: 25px 0px 25px 0px;
}

.systems {
  background-color: #f9f9fb;
  font-family: 'Open Sans', open-sans, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 12px;
  color: #5f697a;
  line-height: 17px;
  margin: 0;
  display: block;
  overflow: auto;
  width: 100%;
}

.item-wrapper {
  display: block;
  position: relative;
  float: left;
  width: 120px;
  border-radius: 4px;
  background: #fff;
  height: 150px;
  text-align: center;
  margin: 0 39px 43px 0;
  -webkit-box-shadow: 0 1px 2px rgba(43, 59, 93, 0.29);
  box-shadow: 0 1px 2px rgba(43, 59, 93, 0.29);
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.item-wrapper h2 {
  color: #424956;
  font-size: 12px;
  font-weight: 400;
  line-height: inherit;
  margin-top: 0px;
}

.systems-wrapper {
  width: 100%;
  margin: 24px 31px;
}

.img-wrapper {
  width: 100%;
  height: 111px;
}

.systems .logo {
  position: absolute;
  width: 70%;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
}

.pull-right.default {
  margin-top: 0;
}

.e-mail-params {
  height: 176px;
}

button {
  margin-top: 20px;
}

.form-group {
  width: 100%;
  height: 50px;
  display: flex;
  margin-top: 0px;
  padding-top: 1px;
  padding-bottom: 1px;
  border-bottom: 1px;
  border-bottom-style: solid;
  border-bottom-color: #dee0e4;
}

.form-group .label {
  margin-top: 20px;
  text-transform: none;
}

.form-group input {
  width: 100%;
  border: 0;
  padding-bottom: 14px;
  padding-top: 18px;
}

input:focus {
  outline: none;
}

.tools {
  left: 594px;
  position: relative;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  width: 46px;
  height: 199px;
}

.element {
  margin-bottom: 16px;
}

.tools .element .button {
  background-color: var(--color-black-lighter);
  fill: #fff;
  stroke: #fff;
  width: 46px;
  height: 46px;
  border-radius: var(--circled);
  border: 0;
  -webkit-box-shadow: var(--elevation-medium);
  box-shadow: var(--elevation-low);
  transform: translateZ(0);
  cursor: pointer;
  outline: 0;
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  align-items: center;
  padding: 0;
  transition: all 0.3s ease-out;
}

.tools .element .button:hover {
  fill: var(--color-black-lighter);
  stroke: var(--color-black-lighter);
  background: #fff;
  -webkit-box-shadow: var(--elevation-high);
  box-shadow: var(--elevation-high);
}

.task-tools i {
  color: black;
}

.pause-code i {
  margin-top: 6px;
  margin-left: 0px;
}

.custom-code-modal-wrapper .left-side-options {
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  list-style: none;
}

.left-side-options ul {
  display: block;
  list-style-type: none;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
  padding-inline-start: 40px;
}

.task-tool .left-side-options {
  position: absolute;
  width: auto;
  text-align: right;
}

.task-tool .left-side-options {
  display: block;
  list-style-type: none;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
  padding-inline-start: 40px;
}

.left-side-options > li.download .run-code:hover {
  background: rgba(0, 0, 0, 0.03);
  overflow: hidden;
  display: block;
  transition: color 0.15s;
  cursor: pointer;
}

.left-side-options > li.download .run-code {
  box-sizing: border-box;
  border: 1px solid #dbdde0;
  border-radius: 50%;
  height: 35px;
  width: 35px;
  top: 11px;
  left: 11px;
  background: #fff;
  overflow: hidden;
  display: block;
  transition: color 0.15s;
}
.left-side-options > li > a,
.left-side-options > span > a {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  border: 1px solid #dbdde0;
  border-radius: 50%;
  height: 42px;
  width: 42px;
  top: 11px;
  left: 11px;
  background: #fff;
  overflow: hidden;
  display: flex;
  -webkit-transition: color 0.15s;
  transition: color 0.15s;
  padding-top: 0;
  padding-left: 0;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.13);
}

.accessibility {
  z-index: 30;
  overflow: hidden !important;
  padding: 0 !important;
  position: absolute !important;
  width: 0px !important;
}

.button--loading::after {
  content: '';
  position: absolute;
  width: 35px;
  height: 35px;
  right: 43px;
  bottom: 5px;
  margin: auto;
  border: 2px solid transparent;
  border-top-color: #181818;
  border-radius: 50%;
  animation: button-loading-spinner 1s ease infinite;
}

@keyframes button-loading-spinner {
  from {
    transform: rotate(0turn);
  }

  to {
    transform: rotate(1turn);
  }
}

.paper-clip {
  padding-left: 5px;
  padding-top: 5px;
}

.data-fields {
  margin-top: 10px;
  margin-left: 1px;
}

.action {
  margin-left: -5px;
  margin-top: 24px;
}

:root {
  --color-pink: #f36;
  --color-pink-darker: #e81e61;
  --color-blue-lighter: #f5fbff;
  --color-blue: #276ee5;
  --color-blue-darker: #1f53d5;
  --color-green-lighter: #f2fbfa;
  --color-green: #09baa6;
  --color-green-darker: #00a08e;
  --color-yellow-lighter: #fffbf4;
  --color-yellow: #ffb631;
  --color-yellow-darker: #e8a01e;
  --color-red-lighter: #fdf3f5;
  --color-red: #e01e3d;
  --color-red-darker: #c60d2b;
  --color-ink-lightest: #9599a2;
  --color-ink-lighter: #6e7077;
  --color-ink: #22252b;
  --color-ink-darker: #17191d;
  --color-gray-lightest: #f8f8fa;
  --color-gray-lighter: #ebecee;
  --color-gray: #dee0e4;
  --color-gray-darker: #c7cad1;
  --color-black-lighter: #182237;
  --color-black: #15181f;
  --color-white: #fff;
  --elevation-low: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.12);
  --elevation-medium: 0 0 0 1px rgba(0, 0, 0, 0.05),
    0 5px 10px rgba(0, 0, 0, 0.15);
  --elevation-high: 0 0 0 1px rgba(0, 0, 0, 0.05),
    0 10px 20px rgba(0, 0, 0, 0.15);
  --rounded: 4px;
  --circled: 50%;
  --pilled: 64px;
}

.tools .element:hover .info-wrapper {
  display: block;
}

.info-wrapper {
  position: absolute;
  display: none;
  will-change: opacity, visibility;
  transition: visibility 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s,
    opacity 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s;
  margin-top: -38px;
  right: 59px;
}

.info-wrapper .info {
  font-family: 'Inv Maison Neue', 'Maison Neue', -apple-system,
    BlinkMacSystemFont, 'Open Sans', open-sans, sans-serif;
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0.125px;
  white-space: nowrap;
  color: white;
  background: rgb(23, 25, 29);
  border-radius: 4px;
  padding: 8px 16px;
}

.toggle-list-wrapper {
  background-color: #ffff;
  display: flex;
  position: absolute;
  right: 59px;
  -webkit-box-shadow: var(--elevation-high);
  box-shadow: var(--elevation-high);
  max-width: 600px;
  overflow-y: scroll;
  z-index: 1;
  margin-top: -93px;
}

.toggle-list-wrapper.data-fields {
  border-radius: 3px;
  display: inline-block;
  clear: both;
  padding: 17px 0 17px 0;
  width: 226px;
  margin-top: 0px;
  margin-left: -186px;
  overflow-y: scroll;
  max-height: 250px;
}

.toggle-list-wrapper.actions {
  border-radius: 3px;
  display: inline-block;
  clear: both;
  padding: 17px 0 17px 0;
  width: 226px;
  margin-top: 0px;
  margin-left: -185px;
  overflow-y: scroll;
  max-height: 250px;
}

.toggle-list-wrapper .item {
  height: 135px;
  cursor: pointer;
  border-right: 1px solid #dbdee5;
  padding: 8px;
  width: 130px;
  text-align: center;
}

.toggle-list-wrapper.data-fields .item,
.toggle-list-wrapper.actions .item {
  width: 100%;
  text-align: left;
  height: -webkit-fit-content;
  height: -moz-fit-content;
  height: fit-content;
  font-weight: 300;
  margin: 0;
  padding: 0;
}

.toggle-list-wrapper.data-fields .item:hover,
.toggle-list-wrapper.actions .item:hover {
  background: #f0f0f0;
}

.toggle-list-wrapper.data-fields .item .label,
.toggle-list-wrapper.actions .item .label {
  font-weight: 300;
  color: #464d5d;
  margin: 0;
}

.toggle-list-wrapper .item .label {
  overflow: hidden;
  font-size: 12px;
  text-transform: none;
  padding: 0;
  margin: 0;
  margin-top: 11px;
  height: 44px;
  padding-left: 14px;
  padding-right: 14px;
  color: #596377;
}

.pretty {
  line-height: 36px;
}

.logo {
  width: 35px;
  margin-top: 24px;
}

.check-box {
  border-style: solid;
  height: 16px;
  width: 16px;
  position: relative;
  top: -127px;
  border-width: 1px;
  border-color: var(--color-black);
  left: 1px;
  background-color: #ffff;
  transition: all 0.3s ease-out;
  border-radius: 50%;
}

.check-box .icon {
  fill: #ffff;
  zoom: 0.6;
  padding-top: 6px;
  padding-left: 3px;
}

.item.checked .check-box {
  background-color: var(--color-black);
}

.search {
  width: 200px;
  padding: 11px 0 11px 0;
  height: 21px;
  margin-left: 12px;
  border-bottom: solid 1px #bdc3c7;
  margin-right: 12px;
  margin-bottom: 12px;
}

.search-icon {
  background-image: url(data:image/png;base64,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);
  background-size: 23px;
  overflow: hidden;
  display: block;
  width: 24px;
  height: 21px;
  float: left;
  background-position: 0px 0px;
}

.search input {
  border: 0;
  width: fit-content;
  height: 13px;
  cursor: initial;
  padding-top: 11px;
  font-size: 13px;
  -webkit-font-smoothing: antialiased;
  font-weight: 300;
}

.attachment {
  overflow-y: scroll;
  margin-right: 0;
  display: block;
  width: 300px;
  top: -217px;
  position: relative;
  height: 147px;
  margin-left: 0;
  float: right;
  text-align: right;
  cursor: auto;
}

.attachment .item {
  height: 135px;
  cursor: auto;
  padding: 8px;
  width: 75px;
  text-align: center;
  display: inline-block;
  vertical-align: top;
}

.attachment .item .label {
  font-size: 12px;
  text-transform: none;
  padding: 0;
  margin: 0;
  margin-top: 11px;
  height: 44px;
  color: #596377;
}

.loading {
  width: 100%;
  height: 100%;
  background-color: #2d2d2d;
  position: relative;
  top: -100%;
  z-index: 3;
  opacity: 0.8;
  color: #ffff;
}

.loading.transparent {
  background-color: #9191912e;
}

.progress-icon-wrapper {
  top: 0;
  bottom: 0;
  height: fit-content;
  width: fit-content;
  left: 0;
  right: 0;
  margin: auto;
  position: absolute;
}

.progress-icon {
  width: 76px;
  height: 76px;
  margin-left: auto;
  top: 0;
  left: 0;
  margin-right: auto;
  margin-top: 0;
}

.progress-icon:after {
  content: ' ';
  display: block;
  width: 70px;
  height: 70px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #ffff;
  border-color: #ffff transparent #ffff transparent;
  animation: progress-icon 1.2s linear infinite;
}

.loading.full-screen-modal {
  width: 100%;
  height: 100%;
  background-color: #ffff;
  position: initial;
  top: -100%;
  z-index: 3;
  opacity: 1;
}

.loading.transparent.full-screen-modal {
  background-color: #9191912e;
}

.progress-icon-wrapper.full-screen-modal {
  top: 0;
  bottom: 67px;
  height: fit-content;
  width: fit-content;
  left: 0;
  right: 0;
  margin: auto;
  position: absolute;
  color: rgb(90, 90, 90);
}

.progress-icon.full-screen-modal {
  width: 76px;
  height: 76px;
  margin-left: auto;
  top: 0;
  left: 0;
  margin-right: auto;
  margin-top: 28px;
}

.progress-icon.full-screen-modal:after {
  content: ' ';
  display: block;
  width: 70px;
  height: 70px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #313745;
  border-color: #313745 transparent #313745 transparent;
  animation: progress-icon 1.2s linear infinite;
}

@keyframes progress-icon {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.modal.task.full-screen {
  width: 100%;
  height: 100%;
  top: 0;
  box-shadow: none;
  border-radius: 0;
}

.custom-code-modal .modal-options {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-top: 0;
  font-size: 17px;
  color: #000;
  position: absolute;
  right: 62px;
  top: 31px;
}
.custom-code-modal .pull-right i {
  cursor: pointer;
}
#codeEditor1 .prism-editor-wrapper.code-editor {
  padding-top: 0;
}

.custom-code-modal .content .tab {
  display: flex;
  flex-direction: column;
  padding: 0 !important;
}

div#codeEditor2 {
  flex: 1 1 auto;
}
::v-deep .toast .toast-header button.close {
  margin-left: auto;
  margin-bottom: 0;
  font-size: 1.3rem;
  line-height: 0;
  color: #000;
  opacity: 0.5;
  border: none;
  background: transparent;
}
::v-deep .toast:not(.show) {
  display: block;
}
.editor-wrapper.hide-overflow {
  overflow-y: hidden;
}

.boostrap-close-btn {
  cursor: pointer;
  display: inline-block;
}
.boostrap-close-btn svg {
  position: relative;
  margin-left: 14px;
}
.listdata {
  display: flex !important;
  flex-direction: column !important;
}

.content .input-attributes textarea {
  resize: none;
  float: left;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  padding-bottom: 15px;
  margin-bottom: 0;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 20px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  height: 200px;
  width: calc(100% - 250px);
  overflow: scroll;
}
.custom-code-modal .custom-code-modal-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}
.custom-code-modal .header {
  width: 100%;
  min-height: 70px;
}
.custom-code-modal .header .tools {
  position: static !important;
  top: 0 !important;
  float: right !important;
  height: 70px !important;
  width: auto !important;
  display: flex !important;
  flex-direction: row !important;
  margin-top: 5px;
}
.custom-code-modal .header .tools .item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 61px;
  margin-left: 10px;
}
.custom-code-modal .round-button {
  cursor: pointer;
  display: block;
  color: #1d1d1f;
  border-radius: 50%;
  background-color: #fff;
  height: 60px;
  width: 60px;
  text-align: center;
  line-height: 60px;
  font-weight: 100;
  box-shadow: 0.5px 0.87px 5px 0 rgba(0, 0, 0, 0.28);
  transition: transform 0.2s ease-in-out;
  font-size: 17px;
}
.custom-code-modal .header .tools .item:hover {
  transform: scale(1.1) rotate(0);
}
.custom-code-modal .header .tools .item img {
  width: 19px;
  height: 19px;
}

.custom-code-modal .custom-code-modal-wrapper .code-tool-wrapper {
  display: none;
}

.custom-code-modal .custom-code-modal-content-wrapper {
  position: relative;
  z-index: 1;
  height: calc(100% - 42px - 36px);
  margin-top: 36px;
}
.run-code {
  position: absolute;
  left: 36px;
  top: 32px;
}
.custom-code-modal .input-attributes {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.custom-code-modal .input-attributes h3,
.custom-code-modal .multiselection-wrap h3 {
  width: 30%;
}
.custom-code-modal .input-attributes h3,
.custom-code-modal .input-attributes label,
.custom-code-modal .multiselection-wrap .input-wrap label,
.custom-code-modal .multiselection-wrap h3 {
  margin-bottom: 0;
  color: #464d5d;
  text-transform: none;
  font-size: 14px;
}

.custom-code-modal .input-attributes input {
  margin-bottom: 0;
  width: 100%;
  height: auto;
  padding: 10px 16px;
}
.custom-code-modal .input-attributes .input-wrap {
  width: 100%;
  display: flex;
}

.custom-code-modal .input-attributes .input-wrap input[type='radio'],
.custom-code-modal .input-attributes .input-wrap input[type='checkbox'] {
  width: auto;
  margin-right: 10px;
}

.custom-code-modal .input-attributes label {
  cursor: pointer;
}
.custom-code-modal .custom-task-inputs .custom-code-modal-content-wrapper {
  height: calc(100% - 65px);
}

.custom-code-modal .content.custom-task-inputs {
  height: 100%;
}
.custom-code-modal .custom-task-detail {
  height: 100%;
}

.custom-code-modal .custom-task-detail .output-area {
  height: 35%;
  padding: 20px 0;
  border-top: 1px solid #eee;
  display: flex;
}

.custom-code-modal .custom-task-detail .output-area .output-read-only {
  height: 100%;
  overflow: auto;
  width: 77%;
}
.custom-code-modal .custom-task-inputs .tab {
  height: 65%;
  overflow-y: auto;
}
.custom-code-modal .output-area h1 {
  margin-bottom: 20px;
  padding: 0;
  text-align: left;
  width: 23%;
}

.custom-code-modal .custom-task-detail .output-area h3 {
  margin-bottom: 0;
  text-align: left;
  font-size: 16px;
  color: #464d5d;
  text-transform: none;
}
.custom-code-modal .multiselection-wrap .input-wrap {
  display: inline-flex;
  align-items: center;
  margin-bottom: 16px;
  margin-right: 16px;
  width: auto;
}

.custom-code-modal .multiselection-wrap .input-wrap input[type='radio'],
.custom-code-modal .multiselection-wrap .input-wrap input[type='checkbox'] {
  height: auto;
  width: auto;
  margin-right: 10px;
  margin-bottom: 0;
}
.custom-code-modal .multiselection-wrap {
  width: 100%;
}
.custom-code-modal
  .outer-loader
  ::v-deep
  .progress-icon-wrapper
  .progress-icon:after {
  border: 2px solid #ffffff;
  border-color: #ffffff transparent #ffffff transparent;
}
.custom-code-modal .outer-loader {
  position: absolute;
  top: 0;
}
.input-area {
  height: calc(100% - (50px));
  overflow: auto;
}

.custom-code-task-wrapper .code-editors-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.custom-code-task-wrapper .log-icon {
  cursor: pointer;
  width: 18px;
}
.custom-code-task-wrapper .code-logs {
  margin-right: 10px;
}
.custom-code-task-wrapper .modal ::v-deep .custom-task-edit-component {
  position: relative;
  z-index: 1;
  height: calc(100% - 42px - 36px);
  margin-top: 17px;
}
</style>
